/**
 * Redux slice (src/store/slices/onlineUsersSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

// src/store/slices/onlineUsersSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import wsService from '../../services/websocketService';

// Async thunk for initializing WebSocket connection and requesting online users
/**
 * Async Redux thunk: Initialize Online Users Tracking. (onlineUsers/initialize)
 *
 * Calls the backend via the shared axios helper and returns a payload consumed by slice extraReducers.
 */
export const initializeOnlineUsersTracking = createAsyncThunk(
  'onlineUsers/initialize',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      if (!wsService.isConnected) {
        throw new Error('WebSocket not connected');
      }

      // Request initial online users list
      wsService.requestOnlineUsers();
      
      // Set up event listeners
      const cleanupFunctions = [
        wsService.addEventListener('onlineUsers', (users) => {
          dispatch(setOnlineUsers(users));
        }),
        
        wsService.addEventListener('userList', (users) => {
          dispatch(setAllUsers(users));
        }),
        
        wsService.addEventListener('userOnline', (userData) => {
          dispatch(addOnlineUser(userData));
        }),
        
        wsService.addEventListener('userOffline', (userData) => {
          dispatch(removeOnlineUser(userData));
        }),
        
        wsService.addEventListener('error', (error) => {
          dispatch(setError(error.message || 'WebSocket error occurred'));
        })
      ];

      // Store cleanup functions for later use
      return { cleanupFunctions };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for refreshing online users data
/**
 * Async Redux thunk: Refresh Online Users. (onlineUsers/refresh)
 *
 * Calls the backend via the shared axios helper and returns a payload consumed by slice extraReducers.
 */
export const refreshOnlineUsers = createAsyncThunk(
  'onlineUsers/refresh',
  async (_, { rejectWithValue }) => {
    try {
      if (!wsService.isConnected) {
        throw new Error('WebSocket not connected');
      }
      
      wsService.requestOnlineUsers();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * RTK slice definition: onlineUsersSlice.
 */
const onlineUsersSlice = createSlice({
  name: 'onlineUsers',
  initialState: {
    onlineUsers: [],
    allUsers: [],
    loading: false,
    error: null,
    lastUpdated: null,
    isInitialized: false,
    connectionStatus: false,
    cleanupFunctions: []
  },
  reducers: {
    /**
     * Reducer: setOnlineUsers.
     */
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
      state.lastUpdated = new Date().toISOString();
      state.error = null;
      
      // Update online status in allUsers as well
      state.allUsers = state.allUsers.map(user => ({
        ...user,
        isOnline: action.payload.some(onlineUser => onlineUser.userId === user.id)
      }));
    },
    
    /**
     * Reducer: setAllUsers.
     */
    setAllUsers: (state, action) => {
      state.allUsers = action.payload;
      state.lastUpdated = new Date().toISOString();
      state.error = null;
    },
    
    /**
     * Reducer: addOnlineUser.
     */
    addOnlineUser: (state, action) => {
      const userData = action.payload;
      
      // Add to online users if not already present
      const existingIndex = state.onlineUsers.findIndex(user => user.userId === userData.userId);
      if (existingIndex === -1) {
        state.onlineUsers.push(userData);
      }
      
      // Update in all users
      const allUserIndex = state.allUsers.findIndex(user => user.id === userData.userId);
      if (allUserIndex !== -1) {
        state.allUsers[allUserIndex] = {
          ...state.allUsers[allUserIndex],
          isOnline: true,
          lastSeen: new Date().toISOString()
        };
      }
      
      state.lastUpdated = new Date().toISOString();
    },
    
    /**
     * Reducer: removeOnlineUser.
     */
    removeOnlineUser: (state, action) => {
      const userData = action.payload;
      
      // Remove from online users
      state.onlineUsers = state.onlineUsers.filter(user => user.userId !== userData.userId);
      
      // Update in all users
      const allUserIndex = state.allUsers.findIndex(user => user.id === userData.userId);
      if (allUserIndex !== -1) {
        state.allUsers[allUserIndex] = {
          ...state.allUsers[allUserIndex],
          isOnline: false,
          lastSeen: userData.lastSeen || new Date().toISOString()
        };
      }
      
      state.lastUpdated = new Date().toISOString();
    },
    
    /**
     * Reducer: setConnectionStatus.
     */
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
      if (!action.payload) {
        // If disconnected, mark all users as offline
        state.onlineUsers = [];
        state.allUsers = state.allUsers.map(user => ({
          ...user,
          isOnline: false
        }));
      }
    },
    
    /**
     * Reducer: setError.
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    
    /**
     * Reducer: clearError.
     */
    clearError: (state) => {
      state.error = null;
    },
    
    /**
     * Reducer: setLoading.
     */
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    /**
     * Reducer: cleanup.
     */
    cleanup: (state) => {
      // Execute cleanup functions
      state.cleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
      
      // Reset state
      state.onlineUsers = [];
      state.allUsers = [];
      state.isInitialized = false;
      state.cleanupFunctions = [];
      state.error = null;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: initializeOnlineUsersTracking.pending
      .addCase(initializeOnlineUsersTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: initializeOnlineUsersTracking.fulfilled
      .addCase(initializeOnlineUsersTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.isInitialized = true;
        state.cleanupFunctions = action.payload.cleanupFunctions || [];
        state.error = null;
      })
      // extraReducers handler: initializeOnlineUsersTracking.rejected
      .addCase(initializeOnlineUsersTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isInitialized = false;
      })
      // extraReducers handler: refreshOnlineUsers.pending
      .addCase(refreshOnlineUsers.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: refreshOnlineUsers.fulfilled
      .addCase(refreshOnlineUsers.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: refreshOnlineUsers.rejected
      .addCase(refreshOnlineUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setOnlineUsers,
  setAllUsers,
  addOnlineUser,
  removeOnlineUser,
  setConnectionStatus,
  setError,
  clearError,
  setLoading,
  cleanup
} = onlineUsersSlice.actions;

// Selectors
/**
 * Selector: return the currently-online user list tracked by this slice.
 */
export const selectOnlineUsers = (state) => state.onlineUsers.onlineUsers;

/**
 * Selector: return the complete user list (online + offline) tracked by this slice.
 */
export const selectAllUsers = (state) => state.onlineUsers.allUsers;

/**
 * Selector: number of currently-online users.
 */
export const selectOnlineUsersCount = (state) => state.onlineUsers.onlineUsers.length;

/**
 * Selector: number of known users in `allUsers`.
 */
export const selectTotalUsersCount = (state) => state.onlineUsers.allUsers.length;

/**
 * Selector: slice-level loading flag used by the UI while updating the lists.
 */
export const selectOnlineUsersLoading = (state) => state.onlineUsers.loading;

/**
 * Selector: last error value stored by this slice (if any).
 */
export const selectOnlineUsersError = (state) => state.onlineUsers.error;

/**
 * Selector: last update timestamp (used to display freshness / trigger refreshes).
 */
export const selectLastUpdated = (state) => state.onlineUsers.lastUpdated;

/**
 * Selector: current connection status string (e.g., connected/disconnected).
 */
export const selectConnectionStatus = (state) => state.onlineUsers.connectionStatus;

/**
 * Selector: whether the online-users system has completed initial bootstrap.
 */
export const selectIsInitialized = (state) => state.onlineUsers.isInitialized;

// Selector for online users by type
/**
 * Selector factory: get online users filtered by `type`.
 *
 * @param {string} type e.g. 'student', 'teacher', 'admin'
 * @returns {(state: any) => any[]}
 */
export const selectOnlineUsersByType = (type) => (state) =>
  state.onlineUsers.onlineUsers.filter(user => user.type === type);

// Selector for users with search and filter
/**
 * Selector factory: filter `allUsers` by search term and optional role/type.
 *
 * @param {string} [searchTerm=''] Case-insensitive search across username/email.
 * @param {string} [filterType='all'] 'all' or a specific type (student/teacher/admin).
 * @returns {(state: any) => any[]}
 */
export const selectFilteredUsers = (searchTerm = '', filterType = 'all') => (state) => {
  return state.onlineUsers.allUsers.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || user.type === filterType;
    return matchesSearch && matchesFilter;
  });
};

// Selector for statistics
/**
 * Selector: compute summary stats for the UI (total/online/offline and per-role counts).
 */
export const selectOnlineUsersStats = (state) => {
  const allUsers = state.onlineUsers.allUsers;
  const onlineUsers = allUsers.filter(user => user.isOnline);
  
  return {
    total: allUsers.length,
    online: onlineUsers.length,
    offline: allUsers.length - onlineUsers.length,
    students: {
      total: allUsers.filter(user => user.type === 'student').length,
      online: onlineUsers.filter(user => user.type === 'student').length
    },
    teachers: {
      total: allUsers.filter(user => user.type === 'teacher').length,
      online: onlineUsers.filter(user => user.type === 'teacher').length
    },
    admins: {
      total: allUsers.filter(user => user.type === 'admin').length,
      online: onlineUsers.filter(user => user.type === 'admin').length
    }
  };
};

export default onlineUsersSlice.reducer;
